import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { deviceKey } from "../lib/device.js";
import { guestErrorMessage, useGuestLocale } from "../lib/i18n.js";
import { PoweredBy } from "../components/PoweredBy.js";
import { LanguagePicker } from "../components/LanguagePicker.js";
import {
  bestMatches,
  printForBox,
  printForImage,
  type FaceHit,
  type FacePrint,
} from "../lib/face-prints.js";
import { MediaTile } from "../../../components/MediaTile.js";
import { Lightbox } from "../../../components/Lightbox.js";
import { Guestbook } from "../../../components/Guestbook.js";
import { AnimatePresence, m } from "motion/react";
import { springs } from "../lib/motion.js";
import type { EventTheme, GalleryAlbum, Media } from "@shared";
import { LIMITS } from "@shared";

interface GalleryEvent {
  readonly slug: string;
  readonly title: string;
  readonly theme: EventTheme;
  readonly allowLikes: boolean;
  readonly allowComments: boolean;
  readonly allowDownloads: boolean;
  /** The host switches the guestbook on per event. */
  readonly allowGuestbook?: boolean;
  /** "" follows the browser; otherwise a shipped dictionary code. */
  readonly guestLanguage?: string;
  /** Present only for a password-protected event. */
  readonly downloadToken?: string;
  readonly status: "draft" | "live" | "ended";
}

/**
 * The public gallery.
 *
 * Read-only, password-gated, and deliberately not the host's gallery: it never
 * shows a pending or rejected photograph, because to a guest those do not
 * exist. The host surface is at /manage/:slug and is behind a session.
 */
const PAGE_SIZE = 60;

/** How many photographs the "find yourself" scan runs against. */
const FACE_CANDIDATES = 24;

/**
 * Readable colours for a host-chosen background.
 *
 * This page sets its own background from `theme.backgroundColor`, which the host
 * picks from a colour input and can be anything. The rest of the app took its
 * text colours from `prefers-color-scheme`, which knows nothing about that
 * choice — so a guest on a dark-mode device viewing any event with the default
 * cream background got near-white text on near-white, and the event title was
 * invisible. The screenshot of a perfectly ordinary event looked broken.
 *
 * So the foreground is derived from the background instead of inherited from the
 * OS. WCAG relative luminance, and the two neutral pairs are chosen for contrast
 * against it rather than merely "dark or light":
 *
 *   light background -> #1a1814 on #fbfaf8, which is 16.9:1
 *   dark background  -> #f2eee8 on #14120f, which is 15.1:1
 *
 * Both comfortably clear the 4.5:1 that body text needs, which a naive
 * "is it light? then use grey" does not.
 */
function themePalette(theme: EventTheme): {
  text: string;
  muted: string;
  surface: string;
  border: string;
} {
  const background = theme.backgroundColor ?? "#fdf8f5";
  return isLight(background)
    ? { text: "#191322", muted: "#7d6a74", surface: "#ffffff", border: "#f1e1e5" }
    : { text: "#f8eff4", muted: "#b9a4af", surface: "#211826", border: "#382a3c" };
}

/** Parse #rgb / #rrggbb. Anything unparseable is treated as light. */
function parseHex(hex: string): [number, number, number] | null {
  const raw = hex.trim().replace(/^#/, "");
  const full = raw.length === 3 ? raw.replace(/./g, (c) => c + c) : raw;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/** WCAG relative luminance. */
function isLight(hex: string): boolean {
  const rgb = parseHex(hex);
  if (!rgb) return true;

  const channel = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2]) > 0.4;
}

export function EventPublic() {  const { slug } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const { host } = useAuth();

  const [event, setEvent] = useState<GalleryEvent | null>(null);
  const [media, setMedia] = useState<Media[]>([]);
  const [total, setTotal] = useState(0);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  // A dictionary key, not a sentence, so it re-renders when the language changes.
  const [error, setError] = useState<"" | "noGallery" | "galleryLoadFailed">("");
  const [wrongPassword, setWrongPassword] = useState(false);
  // Known before the event is: the password refusal says which language the
  // host chose, so the prompt is already in it.
  const [promptLanguage, setPromptLanguage] = useState("");
  const [open, setOpen] = useState<Media | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [downloading, setDownloading] = useState<"all" | "selected" | null>(null);
  const [downloadNotice, setDownloadNotice] = useState("");

  // The host's album boards, as the guest sees them: names and counts only.
  const [albums, setAlbums] = useState<GalleryAlbum[]>([]);
  const [faceStatus, setFaceStatus] = useState("");
  const [faceHits, setFaceHits] = useState<FaceHit[]>([]);
  const [faceBusy, setFaceBusy] = useState(false);

  const supplied = params.get("password") ?? undefined;
  const tag = params.get("tag") ?? "";
  const album = params.get("album") ?? "";

  // Above every early return: this is a hook, and the password prompt and the
  // error states need it as much as the gallery does.
  const locale = useGuestLocale(event?.guestLanguage ?? promptLanguage);
  const { copy } = locale;

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      const result = await api.get<{ event: GalleryEvent; media: Media[]; totalCount: number }>(
        `/api/events/${slug}/gallery?limit=${PAGE_SIZE}${supplied ? `&password=${encodeURIComponent(supplied)}` : ""}${
          tag ? `&tag=${encodeURIComponent(tag)}` : ""
        }${album ? `&album=${encodeURIComponent(album)}` : ""}`,
      );
      setEvent(result.event);
      setMedia(result.media);
      setTotal(result.totalCount);
      setNeedsPassword(false);
      setError("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setNeedsPassword(true);
        setWrongPassword(err.details?.wrongPassword === true);
        if (typeof err.details?.guestLanguage === "string") setPromptLanguage(err.details.guestLanguage);
        return;
      }
      setError(err instanceof ApiError && err.status === 404 ? "noGallery" : "galleryLoadFailed");
    }
  }, [slug, supplied, tag, album]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The album boards for the chip row.
   *
   * Same credentials as the gallery itself — a protected event's album *names*
   * are private too. Failure is silent because an empty chip row degrades to
   * the everything view the gallery already shows.
   */
  useEffect(() => {
    if (!slug || needsPassword) return;
    const query = supplied ? `?password=${encodeURIComponent(supplied)}` : "";
    api
      .get<{ albums: GalleryAlbum[] }>(`/api/events/${slug}/albums${query}`)
      .then((result) => setAlbums(result.albums))
      .catch(() => setAlbums([]));
  }, [slug, needsPassword, supplied]);

  /**
   * Fetch the next page.
   *
   * Offset paging, not cursor paging: it is what the index serves directly, and
   * D1 charges rows read either way. The trade is that a photo uploaded while
   * somebody is scrolling can shift the window by one — harmless here, since new
   * uploads appear at the top and a guest is looking forwards, not backfilling a
   * precise set.
   */
  const loadMore = useCallback(async () => {
    if (!slug || loadingMore || media.length >= total) return;
    setLoadingMore(true);
    try {
      const result = await api.get<{ media: Media[] }>(
        `/api/events/${slug}/gallery?limit=${PAGE_SIZE}&offset=${media.length}${
          supplied ? `&password=${encodeURIComponent(supplied)}` : ""
        }${tag ? `&tag=${encodeURIComponent(tag)}` : ""}${album ? `&album=${encodeURIComponent(album)}` : ""}`,
      );
      setMedia((current) => {
        // Guard against a duplicate when two pages overlap after a new upload.
        const seen = new Set(current.map((item) => item.mediaId));
        return [...current, ...result.media.filter((item) => !seen.has(item.mediaId))];
      });
    } catch {
      // A failed page is not worth replacing the gallery with an error.
    } finally {
      setLoadingMore(false);
    }
  }, [slug, supplied, tag, album, media.length, total, loadingMore]);

  /**
   * Move one photograph along the grid.
   *
   * `media` is newest-first, so "next" is the previous index. The step wraps
   * rather than stopping, because a lightbox that dead-ends on the last photo
   * reads as a broken control.
   *
   * This must stay above the early returns below. It used to sit directly
   * beneath them, which meant the very first render (no event yet, so the
   * `!event` branch returned) called one *fewer* hook than every render after
   * the gallery loaded — and React threw "Rendered more hooks than during the
   * previous render" on the transition, crashing this page every single time it
   * was opened.
   *
   * Nothing catches that in a test suite: the HTTP tests never render React, and
   * the browser suite only drove /wall/:slug. The page you share with every
   * guest was a blank error screen. See scripts/ops/shots.ts, which renders every
   * screen and is how it was found.
   */
  const step = useCallback(
    (delta: number) => {
      setOpen((current) => {
        if (!current || media.length < 2) return current;
        const index = media.findIndex((item) => item.mediaId === current.mediaId);
        if (index === -1) return current;
        const next = (index + delta + media.length) % media.length;
        return media[next] ?? current;
      });
    },
    [media],
  );

  /**
   * Tags present on the loaded page, most common first.
   *
   * This is a navigation aid, not a full facet index: a 400-photo event has
   * sixty tags and nobody scrolls sixty chips. The cap keeps the row to one
   * line on a phone; the filter itself is server-side, so tapping one still
   * searches the whole approved set rather than only what is on screen.
   */
  const tagCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of media) {
      for (const value of item.tags) counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 14);
  }, [media]);

  const chooseTag = (next: string): void => {
    const updated = new URLSearchParams(params);
    if (next) updated.set("tag", next);
    else updated.delete("tag");
    // A tag filter hides photos, and a selection that includes hidden rows is a
    // count the guest cannot reconcile with what they can see. Paging keeps its
    // selection; changing the filter starts a fresh one.
    setSelected(new Set());
    setParams(updated);
  };

  const chooseAlbum = (next: string): void => {
    const updated = new URLSearchParams(params);
    if (next) updated.set("album", next);
    else updated.delete("album");
    // Same rule as tags: the selection described one view; the new view gets
    // its own.
    setSelected(new Set());
    setFaceHits([]);
    setParams(updated);
  };

  if (error) {
    return (
      <div className="guest">
        <header className="guest-top">
          <span className="brand">
            <span className="brand-mark" aria-hidden="true" />
            Eventgram
          </span>
          <LanguagePicker locale={locale} />
        </header>
        <div className="card empty-state">
          <span className="empty-icon" aria-hidden="true">
            🖼️
          </span>
          <h1 style={{ fontSize: "1.5rem" }}>{copy.notFound}</h1>
          <p className="error">{copy[error]}</p>
        </div>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="guest">
        <header className="guest-top">
          <span className="brand">
            <span className="brand-mark" aria-hidden="true" />
            Eventgram
          </span>
          <LanguagePicker locale={locale} />
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const next = new URLSearchParams(params);
            next.set("password", password);
            setParams(next);
          }}
          className="card auth-card stack"
        >
          <p className="eyebrow">{copy.privateGallery}</p>
          <h1>{copy.galleryIsPrivate}</h1>
          <p className="muted">{copy.enterPassword}</p>
          <label htmlFor="gallery-password">{copy.passwordLabel}</label>
          <input
            id="gallery-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={copy.passwordPlaceholder}
            autoFocus
          />
          {wrongPassword ? (
            <p className="error" role="alert">
              {copy.wrongPassword}
            </p>
          ) : null}
          <button type="submit">{copy.openGallery}</button>
        </form>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="guest">
        <header className="guest-top">
          <span className="brand">
            <span className="brand-mark" aria-hidden="true" />
            Eventgram
          </span>
          <LanguagePicker locale={locale} />
        </header>
        <div className="card empty-state">
          <span className="empty-icon" aria-hidden="true">
            📷
          </span>
          <h1 style={{ fontSize: "1.5rem" }}>{copy.openingGallery}</h1>
          <p className="muted">{copy.oneMoment}</p>
        </div>
      </div>
    );
  }

  const showNames = event.theme.showGuestNames;
  const palette = themePalette(event.theme);

  /**
   * The archive URL for this guest.
   *
   * A password event already has a short-lived view token from the gallery
   * response; using it keeps the password out of a URL that lands in browser
   * history. The password fallback only exists for a password event whose
   * gallery response predates the token field.
   */
  const downloadUrl = (ids?: readonly string[]): string => {
    const query = new URLSearchParams();
    if (ids && ids.length > 0) query.set("ids", ids.join(","));
    if (event.downloadToken) query.set("t", event.downloadToken);
    else if (supplied) query.set("password", supplied);
    const suffix = query.toString();
    return `/api/events/${event.slug}/download.zip${suffix ? `?${suffix}` : ""}`;
  };

  /**
   * Start a browser download.
   *
   * The `?check=1` request is a preflight, not the download: it runs the same
   * auth, selection and limit checks and answers JSON, so a 413 or 429 becomes a
   * sentence on the page instead of a failed navigation the guest never sees.
   * The archive itself is a navigation, never `fetch()` + `blob()` — a phone
   * holding a multi-gigabyte album in memory is a crash, not a download.
   */
  const startDownload = async (ids?: readonly string[]): Promise<void> => {
    if (downloading) return;
    const url = downloadUrl(ids);
    setDownloading(ids && ids.length > 0 ? "selected" : "all");
    setDownloadNotice("");

    try {
      const preflightUrl = `${url}${url.includes("?") ? "&" : "?"}check=1`;
      await api.get<{ ok: boolean; files: number; bytes: number }>(preflightUrl);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${event.slug}.zip`;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      setDownloadNotice(copy.downloadStarting);
      // There is no progress event for a browser download. Clearing the button
      // after a beat keeps it from staying disabled for the rest of the visit.
      window.setTimeout(() => setDownloading(null), 1200);
    } catch (err) {
      setDownloading(null);

      if (!(err instanceof ApiError)) {
        setDownloadNotice(copy.downloadFailed);
        return;
      }

      if (err.status === 413) {
        // The album is larger than one archive may be. Selection is the way out,
        // and the server's suggestion says exactly that.
        setSelecting(true);
        setDownloadNotice(copy.downloadTooLarge);
        return;
      }

      if (err.status === 429) {
        const seconds =
          typeof err.details?.retryAfterSeconds === "number"
            ? err.details.retryAfterSeconds
            : null;
        const minutes = seconds === null ? null : Math.max(1, Math.ceil(seconds / 60));
        setDownloadNotice(
          minutes === null ? copy.errRateLimited : locale.plural("downloadWait", minutes),
        );
        return;
      }

      if (err.status === 400 && Array.isArray(err.details?.missingIds)) {
        // A selected photo was deleted or rejected between selection and
        // download. Drop exactly those ids so the next tap works.
        const missing = new Set(
          err.details.missingIds.filter((id): id is string => typeof id === "string"),
        );
        setSelected((current) => new Set([...current].filter((id) => !missing.has(id))));
        setDownloadNotice(copy.downloadMissing);
        return;
      }

      setDownloadNotice(guestErrorMessage(copy, err));
    }
  };

  const toggleSelected = (item: Media): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(item.mediaId)) next.delete(item.mediaId);
      else next.add(item.mediaId);
      return next;
    });
  };

  const cancelSelection = (): void => {
    if (downloading) return;
    setSelecting(false);
    setSelected(new Set());
  };

  const selectedIds = [...selected];

  /**
   * "Find yourself": the guest's selfie never leaves the device.
   *
   * Selfie -> local print; then each photograph that carries face boxes is
   * loaded and its boxes are cropped and printed *on the phone too*. Only the
   * geometry came from the Worker. Computation happens inside one click's
   * worth of waiting because the candidate set is capped at 24 photographs.
   */
  const runFaceSearch = async (file: File): Promise<void> => {
    if (!slug || faceBusy) return;
    setFaceBusy(true);
    setFaceStatus(copy.scanning);
    setFaceHits([]);
    try {
      const selfie = await printForImage(file);
      if (!selfie) {
        setFaceStatus(copy.noMatchYet);
        setFaceBusy(false);
        return;
      }

      const result = await api.get<{ media: Media[] }>(
        `/api/events/${slug}/faces?limit=${FACE_CANDIDATES}${supplied ? `&password=${encodeURIComponent(supplied)}` : ""}`,
      );

      const prints = new Map<string, FacePrint[]>();
      const candidates: FaceHit[] = [];

      for (const item of result.media) {
        if (!Array.isArray(item.faces) || item.faces.length === 0) continue;

        const img = new Image();
        img.src = item.displayUrl;
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // skip that photograph, not the search
        });
        if (!img.complete || !img.naturalWidth) continue;

        const fprints: FacePrint[] = [];
        for (const box of item.faces) {
          const print = await printForBox(img, box);
          if (print) fprints.push(print);
        }
        if (fprints.length > 0) {
          prints.set(item.mediaId, fprints);
          candidates.push({
            mediaId: item.mediaId,
            displayUrl: item.displayUrl,
            thumbUrl: item.thumbUrl,
            score: 1,
          });
        }
      }

      const hits = bestMatches(selfie, candidates, prints);
      setFaceHits(hits);
      setFaceStatus(hits.length === 0 ? copy.noMatchYet : "");
    } catch (err) {
      setFaceStatus(err instanceof ApiError ? guestErrorMessage(copy, err) : copy.noMatchYet);
    } finally {
      setFaceBusy(false);
    }
  };

  return (
    <div
      className="gallery-shell gallery-themed"
      style={
        {
          backgroundColor: event.theme.backgroundColor,
          minHeight: "100%",
          // Overrides, rather than inherits: see themePalette().
          "--text": palette.text,
          "--muted": palette.muted,
          "--surface": palette.surface,
          "--border": palette.border,
        } as React.CSSProperties
      }
    >
      <div className="layout gallery-layout">
        <header className="topbar">
          <span className="brand">
            <span className="brand-mark" aria-hidden="true" />
            Eventgram
          </span>
          <div className="guest-top-tools">
            <LanguagePicker locale={locale} />
            {host ? (
              <a className="button button-secondary" href={`/manage/${event.slug}`}>
                {copy.hostView}
              </a>
            ) : null}
          </div>
        </header>

        <div className="stack" style={{ gap: "1.2rem" }}>
          <div className="gallery-head">
            <p className="eyebrow" style={{ color: "var(--accent)" }}>
              {copy.galleryTitle}
            </p>
            <h1>{event.title}</h1>
            <p className="muted">
              {locale.plural("photoCount", total)}
              {tag ? ` ${locale.t("taggedWith", { tag })}` : ""}
              {event.status === "ended" ? ` — ${copy.eventFinished}` : ""}
            </p>

            {event.allowDownloads && total > 0 ? (
              <div className="gallery-actions">
                <button
                  type="button"
                  onClick={() => void startDownload()}
                  disabled={downloading !== null}
                >
                  {downloading === "all" ? copy.preparingDownload : copy.downloadAll}
                </button>
                {!selecting ? (
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => {
                      setSelecting(true);
                      setDownloadNotice("");
                    }}
                  >
                    {copy.selectPhotos}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          {albums.length > 0 ? (
            <div className="tag-filter" role="group" aria-label={copy.filterByAlbum}>
              <button
                type="button"
                className={`tag-chip${album === "" ? " tag-chip-active" : ""}`}
                aria-pressed={album === ""}
                onClick={() => chooseAlbum("")}
              >
                {copy.all}
              </button>
              {albums.map((entry) => (
                <button
                  key={entry.albumId}
                  type="button"
                  className={`tag-chip${album === entry.albumId ? " tag-chip-active" : ""}`}
                  aria-pressed={album === entry.albumId}
                  onClick={() => chooseAlbum(album === entry.albumId ? "" : entry.albumId)}
                >
                  {entry.name}
                  <span className="tag-count">{entry.mediaCount}</span>
                </button>
              ))}
            </div>
          ) : null}

          {tagCounts.length > 0 ? (
            <div className="tag-filter" role="group" aria-label={copy.filterByTag}>
              <button
                type="button"
                className={`tag-chip${tag ? "" : " tag-chip-active"}`}
                aria-pressed={!tag}
                onClick={() => chooseTag("")}
              >
                {copy.all}
              </button>
              {tagCounts.map(([value, count]) => (
                <button
                  key={value}
                  type="button"
                  className={`tag-chip${tag === value ? " tag-chip-active" : ""}`}
                  aria-pressed={tag === value}
                  onClick={() => chooseTag(tag === value ? "" : value)}
                >
                  {value}
                  <span className="tag-count">{count}</span>
                </button>
              ))}
            </div>
          ) : null}

          {selecting ? (
            <div className="download-toolbar" role="group" aria-label={copy.chooseToDownload}>
              <strong aria-live="polite">{locale.plural("selected", selected.size)}</strong>
              <button
                type="button"
                className="link-button"
                onClick={() => setSelected(new Set(media.map((item) => item.mediaId)))}
                disabled={media.length === 0 || selected.size === media.length}
              >
                {copy.selectAllShown}
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => setSelected(new Set())}
                disabled={selected.size === 0}
              >
                {copy.clear}
              </button>
            </div>
          ) : null}

          {downloadNotice ? (
            <p className="notice download-notice" role="status">
              {downloadNotice}
            </p>
          ) : null}

          {media.length === 0 ? (
            <div className="card empty-state">
              <span className="empty-icon" aria-hidden="true">
                {tag ? "🔎" : "📷"}
              </span>
              <h2>{tag ? locale.t("noTagged", { tag }) : copy.emptyAlbum}</h2>
              <p className="muted">{tag ? copy.tryAnotherTag : copy.emptyHint}</p>
            </div>
          ) : (
            <div className="tile-grid">
              {/*
                Photos rise into view as the guest scrolls, a row at a time
                (the delay follows the column), and when an album or tag
                filter changes, the ones that stay glide to their new places
                while the rest fade out.
              */}
              <AnimatePresence mode="popLayout">
                {media.map((item, index) => (
                  <m.div
                    key={item.mediaId}
                    layout
                    initial={{ opacity: 0, y: 24, scale: 0.96 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true, margin: "0px 0px -40px 0px" }}
                    exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.18 } }}
                    transition={{ ...springs.soft, delay: (index % 4) * 0.06 }}
                  >
                    <MediaTile
                      media={item}
                      showGuestName={showNames}
                      onOpen={setOpen}
                      selectable={selecting}
                      selected={selected.has(item.mediaId)}
                      onToggle={toggleSelected}
                      copy={copy}
                    />
                  </m.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          {media.length < total ? (
            <div className="row" style={{ justifyContent: "center" }}>
              <button
                type="button"
                className="button button-secondary"
                onClick={() => void loadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? copy.loading : locale.t("showMoreLeft", { n: total - media.length })}
              </button>
            </div>
          ) : null}

          {/*
            "Find yourself": the one piece of AI guest value that works with a
            gallery full of stranger's photos. It is opt-in per visit, strictly
            local, and framed as suggestions — see runFaceSearch above.
          */}
          <details className="card" style={{ padding: "1rem" }}>
            <summary style={{ fontWeight: 600, cursor: "pointer" }}>{copy.addYourFace}</summary>
            <div className="stack" style={{ gap: "0.6rem", marginTop: "0.7rem" }}>
              <p className="muted" style={{ margin: 0 }}>
                {copy.selfieHint}
              </p>
              <div className="row">
                <label className="button button-secondary" style={{ margin: 0 }}>
                  {faceBusy ? copy.scanning : copy.takeSelfie}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (file) void runFaceSearch(file);
                    }}
                  />
                </label>
              </div>
              {faceStatus ? <p className="muted" role="status" style={{ margin: 0 }}>{faceStatus}</p> : null}
              {faceHits.length > 0 ? (
                <div className="stack" style={{ gap: "0.4rem" }}>
                  <strong>{copy.matches}</strong>
                  <div className="tile-grid">
                    {faceHits.map((hit) => (
                      <button
                        key={hit.mediaId}
                        type="button"
                        style={{ padding: 0, background: "none", border: "none", cursor: "pointer" }}
                        onClick={() => {
                          const match = media.find((item) => item.mediaId === hit.mediaId);
                          if (match) setOpen(match);
                        }}
                      >
                        <img
                          src={hit.thumbUrl || hit.displayUrl}
                          alt=""
                          loading="lazy"
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", borderRadius: 6 }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </details>

          {event.allowGuestbook ? (
            <Guestbook
              slug={event.slug}
              guestKey={deviceKey()}
              password={supplied}
              compact
              labels={{
                title: copy.guestbook,
                composePlaceholder: copy.guestbookPlaceholder,
                namePlaceholder: copy.namePlaceholder,
                send: copy.send,
                sending: copy.sending,
                record: copy.record,
                stop: copy.stop,
                empty: copy.emptyGuestbook,
                removeMine: copy.removeMine,
                waitingForHost: copy.waitingForHost,
                heldNotice: copy.heldNotice,
                previewHint: copy.previewHint,
                sendVoice: copy.sendVoice,
                recordAgain: copy.recordAgain,
                discard: copy.discard,
                micDenied: copy.micDenied,
                showMore: copy.showMore,
              }}
              formatDate={locale.dateTime}
            />
          ) : null}
        </div>
      </div>

      {selecting ? (
        <div className="download-bar" role="region" aria-label={copy.downloadSelection}>
          <span className="download-bar-count">{locale.plural("photoCount", selected.size)}</span>
          <button
            type="button"
            onClick={() => void startDownload(selectedIds)}
            disabled={selected.size === 0 || downloading !== null}
          >
            {downloading === "selected"
              ? copy.preparingDownload
              : locale.t("downloadSelected", { n: selected.size })}
          </button>
          <button
            type="button"
            className="button button-secondary"
            onClick={cancelSelection}
            disabled={downloading !== null}
          >
            {copy.cancel}
          </button>
        </div>
      ) : null}

      <PoweredBy text={copy.footer} source="gallery" />

      {open ? (
        <Lightbox
          media={open}
          allowComments={event.allowComments}
          allowLikes={event.allowLikes}
          isHost={host !== null}
          onClose={() => setOpen(null)}
          onChange={(next) =>
            setMedia((current) =>
              current.map((item) => (item.mediaId === next.mediaId ? next : item)),
            )
          }
          onDeleted={() => undefined}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          locale={locale}
        />
      ) : null}
    </div>
  );
}
